import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ENTITIES = [
  { key: "project", label: "Projects" },
  { key: "community", label: "Communities" },
];

const SOURCES = [
  { key: "csv", label: "CSV upload", info: "Column headers: name, slug, community, status, starting_price, latitude, longitude, payment_plan, amenities (comma-list)" },
  { key: "excel", label: "Excel (.xlsx) upload", info: "First row = column headers (same field names as CSV)." },
  { key: "json", label: "JSON payload", info: "Paste a JSON array of records to import as drafts." },
  { key: "xml", label: "XML feed", info: "Paste an XML feed with a repeating record tag (e.g. <item>…</item>)." },
  { key: "gsheet", label: "Google Sheets", info: "Publish the sheet as CSV (File → Share → Publish → CSV) and paste the URL." },
  { key: "api", label: "Approved external API", info: "Provide a JSON endpoint (with optional auth header). Data becomes drafts for admin review." },
  { key: "alnair", label: "Alnair (placeholder)", info: "Placeholder for the future official Alnair integration. Activated only when ALNAIR_API_URL & ALNAIR_API_TOKEN env vars are set." },
];

const CSV_TEMPLATE = "name,slug,community,status,starting_price,latitude,longitude,payment_plan,amenities\nSample Tower,sample-tower,Business Bay,off-plan,1900000,25.185,55.265,60/40,\"Infinity pool, Gym\"\n";

export default function AdminImports() {
  const [entity, setEntity] = useState("project");
  const [source, setSource] = useState("csv");
  const [file, setFile] = useState(null);
  const [json, setJson] = useState("");
  const [xml, setXml] = useState("");
  const [recordTag, setRecordTag] = useState("item");
  const [sheetUrl, setSheetUrl] = useState("");
  const [apiUrl, setApiUrl] = useState("");
  const [apiAuth, setApiAuth] = useState("");
  const [apiPath, setApiPath] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true); setResult(null);
    try {
      let r;
      if (source === "csv" || source === "excel") {
        if (!file) { toast.error("Choose a file"); setBusy(false); return; }
        const fd = new FormData();
        fd.append("file", file);
        r = await api.post(`/admin/import/${source}?entity=${entity}`, fd);
      } else if (source === "json") {
        const rows = JSON.parse(json);
        r = await api.post(`/admin/import/json`, { entity, rows });
      } else if (source === "xml") {
        r = await api.post(`/admin/import/xml`, { entity, xml, record_tag: recordTag });
      } else if (source === "gsheet") {
        r = await api.post(`/admin/import/gsheet`, { entity, sheet_url: sheetUrl });
      } else if (source === "api") {
        r = await api.post(`/admin/import/api`, {
          entity, url: apiUrl,
          auth_header: apiAuth || null,
          json_path: apiPath || null,
        });
      } else if (source === "alnair") {
        r = await api.post(`/admin/import/alnair`);
      }
      setResult(r.data);
      toast.success(`Imported: ${r.data.created} · Skipped: ${r.data.skipped}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d : d?.message || "Import failed";
      toast.error(msg);
      setResult(d || { error: err.message });
    } finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "point-penta-projects-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const inputCls = "w-full border border-[#E5E7EB] px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:border-[#001588]";

  return (
    <div className="p-10 max-w-5xl" data-testid="admin-imports">
      <div className="mb-3 text-xs tracking-[0.24em] uppercase text-gray-500">Data pipeline</div>
      <h1 className="font-serif text-4xl mb-2">Data imports</h1>
      <p className="text-sm text-gray-600 max-w-3xl mb-8">
        Import projects and communities from approved sources only. Every imported record lands in <em>Draft</em>
        &nbsp;and requires admin approval before it becomes public. Point Penta does not scrape from Bayut, Property
        Finder, Alnair or any competitor site — this pipeline exists precisely so that Point Penta can consolidate
        data from official developer feeds, spreadsheets or its own CRM.
      </p>

      <div className="bg-white border border-[#E5E7EB] p-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.2em] text-gray-500 block mb-2">Import into</span>
            <select className={inputCls} data-testid="import-entity" value={entity} onChange={(e) => setEntity(e.target.value)}>
              {ENTITIES.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.2em] text-gray-500 block mb-2">Source</span>
            <select className={inputCls} data-testid="import-source" value={source} onChange={(e) => setSource(e.target.value)}>
              {SOURCES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </label>
        </div>

        <p className="text-xs text-gray-500 border-l-2 border-[#001588] pl-3">
          {SOURCES.find((s) => s.key === source)?.info}
        </p>

        {(source === "csv" || source === "excel") && (
          <div className="space-y-3">
            <input
              type="file"
              accept={source === "csv" ? ".csv" : ".xlsx"}
              data-testid="import-file-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block text-sm"
            />
            <button type="button" onClick={downloadTemplate} className="text-xs text-[#001588] underline">
              Download CSV template
            </button>
          </div>
        )}

        {source === "json" && (
          <label className="block">
            <span className="text-xs uppercase tracking-[0.2em] text-gray-500 block mb-2">JSON array</span>
            <textarea rows={8} className={inputCls} value={json} onChange={(e) => setJson(e.target.value)} placeholder='[ { "name": "Sample Tower", "community": "Business Bay", "starting_price": 1900000 } ]' />
          </label>
        )}

        {source === "xml" && (
          <div className="space-y-3">
            <label className="block">
              <span className="text-xs uppercase tracking-[0.2em] text-gray-500 block mb-2">XML</span>
              <textarea rows={8} className={inputCls} value={xml} onChange={(e) => setXml(e.target.value)} placeholder="<feed><item><name>...</name></item></feed>" />
            </label>
            <label className="block max-w-xs">
              <span className="text-xs uppercase tracking-[0.2em] text-gray-500 block mb-2">Record tag</span>
              <input className={inputCls} value={recordTag} onChange={(e) => setRecordTag(e.target.value)} />
            </label>
          </div>
        )}

        {source === "gsheet" && (
          <label className="block">
            <span className="text-xs uppercase tracking-[0.2em] text-gray-500 block mb-2">Google Sheets publish-to-web CSV URL</span>
            <input className={inputCls} value={sheetUrl} onChange={(e) => setSheetUrl(e.target.value)} placeholder="https://docs.google.com/spreadsheets/…/pub?output=csv" />
          </label>
        )}

        {source === "api" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block md:col-span-2">
              <span className="text-xs uppercase tracking-[0.2em] text-gray-500 block mb-2">API URL (must be JSON)</span>
              <input className={inputCls} value={apiUrl} onChange={(e) => setApiUrl(e.target.value)} placeholder="https://developer-feed.example.com/projects" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-[0.2em] text-gray-500 block mb-2">Auth header (optional)</span>
              <input className={inputCls} value={apiAuth} onChange={(e) => setApiAuth(e.target.value)} placeholder="Bearer …" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-[0.2em] text-gray-500 block mb-2">JSON path (optional)</span>
              <input className={inputCls} value={apiPath} onChange={(e) => setApiPath(e.target.value)} placeholder="data.items" />
            </label>
          </div>
        )}

        {source === "alnair" && (
          <div className="p-4 bg-amber-50 border border-amber-200 text-amber-900 text-sm">
            Alnair integration is <strong>not yet connected</strong>. When Point Penta receives official API
            credentials from Alnair, set <code>ALNAIR_API_URL</code> and <code>ALNAIR_API_TOKEN</code> in the
            backend environment and this endpoint will import projects as drafts. No scraping is performed.
          </div>
        )}

        <div className="flex justify-end pt-2">
          <button
            type="button"
            onClick={run}
            disabled={busy}
            data-testid="import-run-btn"
            className="px-6 py-3 bg-[#001588] text-white text-sm uppercase tracking-[0.2em] hover:bg-black disabled:opacity-60"
          >
            {busy ? "Importing…" : "Import as drafts"}
          </button>
        </div>

        {result && (
          <pre data-testid="import-result" className="mt-4 p-4 bg-gray-900 text-emerald-300 text-xs overflow-auto rounded">
            {JSON.stringify(result, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}
